// Ported from dev-scrips-pm-react src/documents/services/use-document-row-actions.ts (PROD-1119 documents module).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { PatientDocumentModel } from '@/documents/types/document';

/**
 * Row-action mutations for the Docs tab (PROD-797 PR 5).
 *
 * Mark-as-Reviewed and Archive both reuse the same
 * `POST /api/Patients/AddUpdatePatientDocument` endpoint as create —
 * they're full-document re-saves with one or two overlaid fields.
 * The server inspects the changed fields and applies the action.
 *
 * Wire-shape contracts dev-curl verified 2026-05-15:
 *
 * **Mark as Reviewed** — set `reviewedById` to the current user's id,
 * leave `reviewedByName` and `reviewedOn` `null`. Server fills the
 * name + timestamp from the auth headers + now() on save.
 *   ```json
 *   { ...existingBody, reviewedById: "<currentUserId>", reviewedByName: null, reviewedOn: null }
 *   ```
 *
 * **Archive** — set `isDeleted: true`. Prior review state (including
 * the populated `reviewedByName` + `reviewedOn` the server filled
 * during a previous Mark-Reviewed call) is preserved on the wire.
 *   ```json
 *   { ...existingBody, isDeleted: true }
 *   ```
 *
 * Both hooks accept the full existing `PatientDocumentModel` from
 * the list cache as input — the caller does NOT need to refetch
 * before invoking. Both invalidate `['patient-documents']` on
 * success so the list refreshes automatically.
 *
 * Why not split into smaller mutations? The wire shape genuinely is
 * "full body with overlay". Trying to PATCH only the changed field
 * would diverge from the verified Flutter behaviour and risk
 * server-side validation rejecting a sparse body.
 *
 * @see {@link ../../../docs/CONVERSION_NOTES_PROD-797.md} (PR 5 — row actions)
 */

interface SaveDocumentResponse {
  id?: string;
}

interface MarkReviewedVariables {
  /** The document as it exists today, from the list query cache. */
  document: PatientDocumentModel;
  /** The current user's id — becomes `reviewedById` on the wire. */
  reviewerId: string;
}

/**
 * Mark a patient document as reviewed. Server fills in
 * `reviewedByName` and `reviewedOn` from the auth headers + now().
 */
export function useMarkDocumentReviewed() {
  const qc = useQueryClient();
  return useMutation<SaveDocumentResponse, Error, MarkReviewedVariables>({
    mutationFn: ({ document, reviewerId }) => {
      const body: PatientDocumentModel = {
        ...document,
        reviewedById: reviewerId,
        reviewedByName: null,
        reviewedOn: null,
      };
      return post<SaveDocumentResponse>(
        '/api/Patients/AddUpdatePatientDocument',
        body,
      );
    },
    onSuccess: () => {
      // **Prefix invalidation contract** (Samer PR #260 review §3):
      // the `['patient-documents', ...]` prefix covers BOTH list
      // query-key shapes that exist today — verified by grep
      // 2026-05-20:
      //
      //   1. Chart-tab list (`@/patients/services/use-patient-documents`):
      //      ['patient-documents', patientId, pageNumber, pageSize]
      //
      //   2. Documents-module list (`@/documents/services/use-patient-documents`):
      //      ['patient-documents', patientId, encounterId, startDate,
      //       endDate, fileType, uploadedBy, pageNumber, pageSize,
      //       isArchived]
      //
      // React Query's `invalidateQueries({queryKey: ['patient-documents']})`
      // does a PREFIX match — it invalidates every key whose first
      // element is `'patient-documents'`, regardless of further
      // elements. Both shapes above start with `'patient-documents'`,
      // so both refetch after a row-action save.
      //
      // **Drift risk**: if a future query hook uses a different
      // top-level slug (e.g. `'patientDocuments'` camelCase), prefix
      // invalidation silently misses it. Lock point: ensure new
      // document-related queryKeys ALWAYS start with the literal
      // `'patient-documents'` string. If a future cache-key
      // refactor changes the shape, update the comments in both
      // `use-patient-documents.ts` files AND this prefix in
      // lockstep.
      void qc.invalidateQueries({ queryKey: ['patient-documents'] });
    },
  });
}

interface ArchiveVariables {
  document: PatientDocumentModel;
  /**
   * Destination archive state (PROD-900).
   * - `'archived'` (default) → `isDeleted: true`  (archive flow).
   * - `'active'`              → `isDeleted: false` (unarchive flow).
   *
   * Mirrors Flutter `document_list_widget.dart:260-262` — same callback
   * dispatches both states; the row's current `isDeleted` value
   * determines the menu label ("Archive" vs "Unarchive"). The toast
   * after action is decided by the caller (this hook has no display
   * concern).
   */
  to?: 'archived' | 'active';
}

/**
 * Toggle the archive state on a patient document. Soft-delete via
 * `isDeleted: true` (archive) or `isDeleted: false` (unarchive —
 * PROD-900). The document remains in the database for audit / restore
 * either way.
 *
 * Active vs archived lists are filtered by the `isArchived` query
 * param on `GetPatientDocuments` (see `use-patient-documents.ts`).
 */
export function useArchiveDocument() {
  const qc = useQueryClient();
  return useMutation<SaveDocumentResponse, Error, ArchiveVariables>({
    mutationFn: ({ document, to = 'archived' }) => {
      const body: PatientDocumentModel = {
        ...document,
        isDeleted: to === 'archived',
      };
      return post<SaveDocumentResponse>(
        '/api/Patients/AddUpdatePatientDocument',
        body,
      );
    },
    onSuccess: () => {
      // **Prefix invalidation contract** (Samer PR #260 review §3):
      // the `['patient-documents', ...]` prefix covers BOTH list
      // query-key shapes that exist today — verified by grep
      // 2026-05-20:
      //
      //   1. Chart-tab list (`@/patients/services/use-patient-documents`):
      //      ['patient-documents', patientId, pageNumber, pageSize]
      //
      //   2. Documents-module list (`@/documents/services/use-patient-documents`):
      //      ['patient-documents', patientId, encounterId, startDate,
      //       endDate, fileType, uploadedBy, pageNumber, pageSize,
      //       isArchived]
      //
      // React Query's `invalidateQueries({queryKey: ['patient-documents']})`
      // does a PREFIX match — it invalidates every key whose first
      // element is `'patient-documents'`, regardless of further
      // elements. Both shapes above start with `'patient-documents'`,
      // so both refetch after a row-action save.
      //
      // **Drift risk**: if a future query hook uses a different
      // top-level slug (e.g. `'patientDocuments'` camelCase), prefix
      // invalidation silently misses it. Lock point: ensure new
      // document-related queryKeys ALWAYS start with the literal
      // `'patient-documents'` string. If a future cache-key
      // refactor changes the shape, update the comments in both
      // `use-patient-documents.ts` files AND this prefix in
      // lockstep.
      void qc.invalidateQueries({ queryKey: ['patient-documents'] });
    },
  });
}
